import { cloneDeep, merge, uniq } from "lodash-es";
import { BehaviorSubject, combineLatest, Observable } from "rxjs";
import { map, switchMap } from "rxjs/operators";

import { CONFIG_ERROR_ATLAS } from "./config.const";
import { ConfigBase, ConfigQuery, TenantBase } from "./config.model";

export class ConfigService<C extends TenantBase<C>> {
	private readonly configStream$: BehaviorSubject<ConfigBase<C>>;
	private readonly jurisdictionStream$: BehaviorSubject<string>;
	private readonly dimensionsStream$: BehaviorSubject<string[]>;

	constructor(
		private configBase: ConfigBase<C>,
		private jurisdiction: string,
		private dimensions: string[] | undefined,
		private tenant: string,
		private enabledPreprocessor = false
	) {
		this.configStream$ = new BehaviorSubject<ConfigBase<C>>(
			enabledPreprocessor ? this.processConfig() : cloneDeep(this.configBase)
		);
		this.jurisdictionStream$ = new BehaviorSubject<string>(this.jurisdiction);
		this.dimensionsStream$ = new BehaviorSubject<string[]>(this.dimensions || []);
	}

	get<T>(query: ConfigQuery<C, T>): T | undefined;

	get<T>(query: ConfigQuery<C, T>, defaultValue: T): T;

	get<T>(query: ConfigQuery<C, T>, defaultValue?: T): T;

	get<T>(query: ConfigQuery<C, T>, defaultValue?: T): T | undefined {
		const tenant = this.getTenantBase(this.configStream$.value);
		const value = this.getValue(this.jurisdiction, tenant, query, defaultValue);

		if (this.enabledPreprocessor) {
			return value;
		}

		return !!this.dimensions && this.dimensions.length
			? this.processDimensions(value, this.jurisdiction, this.dimensions, tenant, query)
			: value;
	}

	get$<T>(query: ConfigQuery<C, T>): Observable<T | undefined>;

	get$<T>(query: ConfigQuery<C, T>, defaultValue: T): Observable<T>;

	get$<T>(query: ConfigQuery<C, T>, defaultValue?: T): Observable<T>;

	get$<T>(query: ConfigQuery<C, T>, defaultValue?: T): Observable<T | undefined> {
		return this.configStream$.pipe(
			switchMap((config) =>
				combineLatest([this.jurisdictionStream$, this.dimensionsStream$]).pipe(
					map(
						([jurisdiction, dimensions]) =>
							[config, jurisdiction, dimensions] as [ConfigBase<C>, string, string[]]
					)
				)
			),
			map(
				([config, jurisdiction, dimensions]) =>
					[this.getTenantBase(config), jurisdiction, dimensions] as [C, string, string[]]
			),
			map(([tenant, jurisdiction, dimensions]) => {
				const value = this.getValue(jurisdiction, tenant, query, defaultValue);

				if (this.enabledPreprocessor) {
					return value;
				}

				return this.processDimensions(value, jurisdiction, dimensions, tenant, query);
			})
		);
	}

	updateConfig(configBase: ConfigBase<C>) {
		this.configBase = configBase;

		this.configStream$.next(this.enabledPreprocessor ? this.processConfig() : cloneDeep(configBase));
	}

	updateJurisdiction(jurisdiction: string) {
		this.jurisdiction = jurisdiction;

		if (this.enabledPreprocessor) {
			this.configStream$.next(this.processConfig());
		}

		this.jurisdictionStream$.next(jurisdiction);
	}

	updateDimensions(...dimensions: string[]) {
		this.dimensions = dimensions;

		if (this.enabledPreprocessor) {
			this.configStream$.next(this.processConfig());
		}

		this.dimensionsStream$.next(uniq(dimensions) || []);
	}

	private processDimensions<T>(
		inputValue: T,
		jurisdiction: string,
		dimensions: string[],
		tenant: C,
		query: ConfigQuery<C, T>
	) {
		if (!tenant.$dimensions) {
			return inputValue;
		}

		for (const dimension of dimensions) {
			if (!tenant.$dimensions[dimension]) {
				continue;
			}

			try {
				const dimValue = this.getValue(jurisdiction, this.ensureTenant(tenant.$dimensions[dimension]), query);
				if (dimValue === undefined) {
					continue;
				}

				inputValue = dimValue;
			} catch {
				/* noop */
			}
		}

		return inputValue;
	}

	private getValue<T>(jurisdiction: string, tenant: C, query: ConfigQuery<C, T>, defaultValue?: T): T | undefined {
		let value: T | undefined;

		try {
			if (this.enabledPreprocessor) {
				value = query(tenant);
			} else {
				if (jurisdiction) {
					// resolve for a jurisdiction
					if (tenant.$jurisdictions && tenant.$jurisdictions[jurisdiction]) {
						try {
							value = query(this.ensureTenant(tenant.$jurisdictions[jurisdiction]));
						} catch {
							/* noop */
						}
					}

					// legacy support, will be removed when vNext is fully deployed
					const legacyTenantJurisdiction = (tenant as any).jurisdiction;
					if (value === undefined && legacyTenantJurisdiction && legacyTenantJurisdiction[jurisdiction]) {
						try {
							value = query(legacyTenantJurisdiction[jurisdiction].jurisdiction as C);
						} catch {
							/* noop */
						}
					}
				}

				if (value === undefined) {
					value = query(tenant);
				}
			}
		} catch {
			/* noop */
		}

		return value !== undefined ? value : defaultValue;
	}

	private getTenantBase(configBase: ConfigBase<C>): C {
		if (!configBase.tenants.hasOwnProperty(this.tenant)) {
			throw new Error(CONFIG_ERROR_ATLAS.missingTenant);
		}

		return configBase.tenants[this.tenant];
	}

	private processConfig(): ConfigBase<C> {
		const configBaseClone = cloneDeep(this.configBase);
		const currentTenantConfig = this.getTenantBase(configBaseClone);

		/* LEGACY CONFIG SUPPORT */
		let currentJurisdiction =
			this.jurisdiction && (currentTenantConfig as any).jurisdiction
				? (currentTenantConfig as any).jurisdiction[this.jurisdiction]
				: undefined;
		currentJurisdiction = currentJurisdiction ? currentJurisdiction.jurisdiction : undefined;
		/* LEGACY CONFIG SUPPORT */

		if (!currentJurisdiction) {
			currentJurisdiction =
				this.jurisdiction && currentTenantConfig.$jurisdictions
					? currentTenantConfig.$jurisdictions[this.jurisdiction]
					: {};
		}

		const currentDimensions =
			this.dimensions && this.dimensions.length && currentTenantConfig.$dimensions
				? this.dimensions!.map((x) => currentTenantConfig.$dimensions![x]).filter((x) => x)
				: [];

		const newTenant = merge(currentTenantConfig, currentJurisdiction || {}, ...currentDimensions);

		return {
			name: currentDimensions.reduce((x, y) => `${y} ${x}`, `${currentJurisdiction}`),
			environment: "",
			version: "",
			tenants: {
				[`${this.tenant}`]: newTenant,
			},
		};
	}

	private ensureTenant(tenantLike: unknown): C {
		return tenantLike as C;
	}
}
